import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErrorDetail } from "@/lib/api";

export default function AdminReset() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center px-5 py-10" data-testid="admin-reset">
      {token ? <SetNewPassword token={token} /> : <RequestReset />}
    </div>
  );
}

function RequestReset() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm bg-peach/50 border border-taupe/30 rounded-3xl p-8" data-testid="reset-request-form">
      <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-14 mb-6" />
      <h1 className="font-display text-3xl mb-2">Recupera l'accesso</h1>
      <p className="text-sm text-charcoal/60 mb-8">
        Inserisci l'email dell'account amministratore oppure il numero di telefono dello studio: ti inviamo un
        collegamento valido 30 minuti per scegliere una nuova password.
      </p>
      {done ? (
        <div className="bg-white/70 border border-taupe/25 rounded-2xl p-5 text-sm text-charcoal/80 mb-6" data-testid="reset-request-sent">
          <p className="font-medium text-deeptaupe mb-1">Richiesta inviata.</p>
          Se email o telefono corrispondono allo studio, entro pochi minuti arriva l'email con il collegamento
          all'indirizzo dell'account: controlla anche la cartella spam.
        </div>
      ) : (
        <>
          <label className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Email o telefono dello studio</label>
          <input
            type="text"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="es. nome@email.it oppure +39 379 316 5184"
            data-testid="reset-request-email"
            className="w-full rounded-full border border-taupe/40 bg-ivory px-5 py-3 mb-5 text-sm focus:border-deeptaupe focus:outline-none"
          />
          {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="reset-request-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-request-submit"
            className="w-full rounded-full bg-deeptaupe text-ivory text-[13px] uppercase tracking-widest2 font-medium py-4 transition-colors duration-300 hover:bg-charcoal disabled:opacity-50"
          >
            {busy ? "Invio…" : "Invia il collegamento"}
          </button>
        </>
      )}
      <Link to="/admin" data-testid="reset-back-to-login" className="block mt-6 text-center text-[12px] uppercase tracking-widest2 text-charcoal/50 hover:text-deeptaupe">
        Torna al login
      </Link>
    </form>
  );
}

function SetNewPassword({ token }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Le due password non coincidono");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(apiErrorDetail(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full max-w-sm bg-peach/50 border border-taupe/30 rounded-3xl p-8" data-testid="reset-set-form">
      <img src="/images/logo-transparent.png" alt="K Nutrition" className="h-14 mb-6" />
      <h1 className="font-display text-3xl mb-2">Nuova password</h1>
      <p className="text-sm text-charcoal/60 mb-8">Scegli una nuova password di almeno 8 caratteri.</p>
      {done ? (
        <div className="bg-white/70 border border-taupe/25 rounded-2xl p-5 text-sm text-charcoal/80 mb-6" data-testid="reset-done">
          <p className="font-medium text-deeptaupe mb-1">Password aggiornata.</p>
          Ora puoi accedere al pannello con la nuova password.
        </div>
      ) : (
        <>
          <label className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Nuova password</label>
          <input
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-new-password"
            className="w-full rounded-full border border-taupe/40 bg-ivory px-5 py-3 mb-4 text-sm focus:border-deeptaupe focus:outline-none"
          />
          <label className="block text-[12px] uppercase tracking-widest2 font-medium text-deeptaupe mb-2">Conferma nuova password</label>
          <input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm-password"
            className="w-full rounded-full border border-taupe/40 bg-ivory px-5 py-3 mb-5 text-sm focus:border-deeptaupe focus:outline-none"
          />
          {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5" data-testid="reset-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="reset-set-submit"
            className="w-full rounded-full bg-deeptaupe text-ivory text-[13px] uppercase tracking-widest2 font-medium py-4 transition-colors duration-300 hover:bg-charcoal disabled:opacity-50"
          >
            {busy ? "Salvataggio…" : "Imposta la nuova password"}
          </button>
        </>
      )}
      <Link to="/admin" data-testid="reset-goto-login" className="block mt-6 text-center text-[12px] uppercase tracking-widest2 text-charcoal/50 hover:text-deeptaupe">
        Vai al login
      </Link>
    </form>
  );
}
